import { useEffect, useRef, useState, useCallback } from 'react'
import styles from '../TrainMachine.module.css'
import renderImage from '../../../../source/importImg'
import { useIndexedDB } from 'react-indexed-db'
import { drawSpectrogram } from './Spectrogram'
import { processAudioFile } from './uploadeAudioFile'
import { detectHumanVoice } from '../ai/detectHumanVoice'
import { vadDetector } from '../audio/vadDetector'
import { processAudioChunk } from '../pipeline/processAudio'
import RecordingControls from './RecordingControls'
import SampleGrid from './SampleGrid'
import renderPrgImage from '../../../../source/programImg'

function AudioModule({
    className = '',
    isShow = false,
    handleDialogModalClose = () => {},
    classes,
    setClasses,
    audioUpload = false,
    currentModelId,
}) {
    const [isRecording, setIsRecording] = useState(false)
    const [recognizer, setRecognizer] = useState(null)
    const [isModelLoading, setIsModelLoading] = useState(false)
    const [capturedSamples, setCapturedSamples] = useState([])
    const [micError, setMicError] = useState('')
    const [selectedFiles, setSelectedFiles] = useState([])
    const { add, getAll, deleteRecord } = useIndexedDB('Samples')
    const savedFacesDb = useIndexedDB('SavedFaces')

    const recognizerRef = useRef(null)
    const timerIntervalRef = useRef(null)
    const secondBufferRef = useRef([])
    const canvasRef = useRef(null)
    const lastIsHumanRef = useRef(false)
    const capturedSamplesRef = useRef([])

    const stateRef = useRef({ classes, className, currentModelId, capturedSamples: [] })

    useEffect(() => {
        stateRef.current = { classes, className, currentModelId, capturedSamples }
    }, [classes, className, currentModelId, capturedSamples])

    const loadSamples = useCallback(async () => {
        try {
            if (!currentModelId || !className) return
            const allSamples = await getAll()
            const filtered = allSamples.filter(
                (s) =>
                    s.modelId === currentModelId &&
                    s.classId === className &&
                    s.sampleType !== 'Image'
            )
            setCapturedSamples(filtered)
            capturedSamplesRef.current = filtered
        } catch (error) {
            console.error('Error loading samples:', error)
        }
    }, [getAll, currentModelId, className])

    useEffect(() => {
        if (isShow && currentModelId && className) loadSamples()
    }, [isShow, currentModelId, className, loadSamples])

    useEffect(() => {
        const initModel = async () => {
            try {
                setIsModelLoading(true)

                // Initialize VAD system
                await vadDetector.initializeVAD({
                    onSpeechStart: () => {
                        lastIsHumanRef.current = true
                    },
                    onSpeechEnd: () => {
                        lastIsHumanRef.current = false
                    },
                })

                const { loadHumanVoiceModel } =
                    await import('../ai/detectHumanVoice')
                const instance = await loadHumanVoiceModel()
                setRecognizer(instance)
                recognizerRef.current = instance
            } catch (error) {
                console.error('Error loading audio model:', error)
                setMicError('Microphone access denied or model load failed. Please ensure your mic is connected and permissions are granted.')
            } finally {
                setIsModelLoading(false)
            }
        }
        if (isShow && !recognizer) initModel()
        return () => {
            if (isRecording) stopRecording()
        }
    }, [isShow, recognizer])

    const updateReduxClassState = useCallback(
        async (samples, finalUpdate = false, consensusCategory = null) => {
            const { classes: currentClasses, className: currentClassId } =
                stateRef.current

            // CRITICAL: Prevent creating empty classes if ID is missing
            if (!currentClassId || !currentClasses[currentClassId]) {
                console.warn('Skipping Redux update: invalid class ID', currentClassId)
                return
            }

            const data = {
                ...currentClasses[currentClassId],
                capturedAudioCount: samples.length,
            }

            if (finalUpdate && consensusCategory) {
                data.CategoryName = consensusCategory

                // Update IndexedDB (SavedFaces store)
                try {
                    const allSavedFaces = await savedFacesDb.getAll()
                    const existingRecord = allSavedFaces.find(
                        (item) => item.id === currentClasses[currentClassId].id
                    )

                    if (existingRecord) {
                        await savedFacesDb.update({
                            ...existingRecord,
                            CategoryName: consensusCategory,
                        })
                    }
                } catch (error) {
                    console.error('Error updating SavedFaces in updateReduxClassState:', error)
                }
            }

            setClasses({
                key: currentClassId,
                data: data,
                classType: 'AudioClasses',
            })
        },
        [setClasses, savedFacesDb]
    )

    const lastIsHumanTFRef = useRef(false)

    const startRecording = async () => {
        if (!recognizerRef.current)
            return setMicError('Please connect the mic to your PC')

        setMicError('')
        setIsRecording(true)
        lastIsHumanRef.current = false
        lastIsHumanTFRef.current = false

        // Start VAD parallel listener
        try {
            await vadDetector.startVAD()
        } catch (err) {
            console.error('VAD failed to start:', err)
            setMicError('Microphone access denied or error starting VAD.')
            setIsRecording(false)
            return
        }

        timerIntervalRef.current = setInterval(async () => {
            const bufferLen = secondBufferRef.current.length
            if (bufferLen < 4) return

            const numBatches = Math.min(Math.floor(bufferLen / 4), 5)
            // Process all batches sequentially then do ONE React state update.
            // Previously each saveBatch called setCapturedSamples individually,
            // causing up to 5 re-renders/sec of the full sample grid (O(N) each).
            for (let i = 0; i < numBatches; i++) {
                await saveBatch(secondBufferRef.current.splice(0, 4), lastIsHumanTFRef.current)
            }
            // Single state update for all batches saved this tick
            setCapturedSamples(capturedSamplesRef.current)
        }, 1000)

        try {
            await recognizerRef.current.listen(
                (result) => {
                    // Combine TF scores with VAD state in detectHumanVoice
                    const detection = detectHumanVoice(result.scores)
                    lastIsHumanTFRef.current = detection.label === 'human'

                    // Schedule on rAF so canvas work never blocks the audio callback
                    const snapData = result.spectrogram.data
                    const snapSize = result.spectrogram.frameSize
                    requestAnimationFrame(() => drawSpectrogram(snapData, snapSize, canvasRef))

                    if (secondBufferRef.current.length < 100) {
                        secondBufferRef.current.push({
                            data: result.spectrogram.data,
                            frameSize: result.spectrogram.frameSize,
                        })
                    }
                },
                {
                    includeSpectrogram: true,
                    invokeCallbackOnNoiseAndUnknown: true,
                    overlapFactor: 0.75,
                }
            )
        } catch (error) {
            console.error('Microphone error:', error)
            setMicError('Microphone error: ' + (error.message || 'Unknown error'))
            stopRecording()
        }
    }

    const stopRecording = async () => {
        if (!isRecording) return
        setIsRecording(false)
        if (timerIntervalRef.current) clearInterval(timerIntervalRef.current)

        // Guard: stopListening throws "Cannot stop streaming when streaming is
        // not ongoing" if the recognizer state and isRecording drifted apart.
        try {
            if (
                recognizerRef.current &&
                (typeof recognizerRef.current.isListening !== 'function' ||
                    recognizerRef.current.isListening())
            ) {
                recognizerRef.current.stopListening()
            }
        } catch (e) {
            /* already stopped */
        }
        vadDetector.stopVAD()

        // Wait a bit for final batches to be processed if any
        setTimeout(async () => {
            try {
                const { classes: currentClasses, className: currentClassId } =
                    stateRef.current
                
                if (!currentClassId || !currentClasses[currentClassId]) return

                const currentSamples = capturedSamplesRef.current
                const humanSamplesCount = currentSamples.filter(
                    (s) => s.isHumanCandidate || s.isHumanFromTF
                ).length
                
                const isHuman = currentSamples.length > 0 && 
                                humanSamplesCount > currentSamples.length * 0.3
                
                const categoryName = isHuman ? 'Human' : 'Non Human'

                // Update Redux once with final consensus (this now also updates IndexedDB)
                updateReduxClassState(currentSamples, true, categoryName)
            } catch (error) {
                console.error('Error in stopRecording detection:', error)
            }
        }, 600)
    }

    const saveBatch = async (batch, isHumanFromTF) => {
        const { currentModelId, className: classId } = stateRef.current
        if (!classId) return

        const sample = await processAudioChunk(batch, currentModelId, classId, isHumanFromTF)
        const id = await add(sample)
        // Push directly — no state update here; the interval caller does ONE update per tick
        capturedSamplesRef.current = [...capturedSamplesRef.current, { ...sample, id }]
    }

    const removeSample = async (id) => {
        await deleteRecord(id)
        capturedSamplesRef.current = capturedSamplesRef.current.filter((s) => s.id !== id)
        setCapturedSamples(capturedSamplesRef.current)
        updateReduxClassState(capturedSamplesRef.current)
    }

    return (
        isShow && (
            <div className={styles.webCamModel}>
                <div className={styles.webCamModelInner}>
                    <img
                        className={styles.WebCamModelCloseBtn}
                        src={renderImage('clos')}
                        onClick={() => {
                            if (isRecording) stopRecording()
                            handleDialogModalClose()
                            setSelectedFiles([])
                        }}
                        alt="Close"
                    />
                    <div className={styles.audioModuleContent}>
                        <h2 style={{ paddingBlock:'5px'}}>Audio Training: {classes[className]?.className}</h2>

                        {audioUpload ? (
                            <div
                                className={styles.webCamModelUploadDiv}
                                onClick={() =>
                                    document.getElementById('audio-up').click()
                                }
                            >
                                <input
                                    id="audio-up"
                                    type="file"
                                    hidden
                                    accept="audio/*"
                                    multiple
                                    onChange={(e) => {
                                        const files = Array.from(e.target.files)
                                        setSelectedFiles(files)
                                        files.forEach((file) =>
                                            processAudioFile(file, {
                                                stateRef,
                                                add,
                                                setCapturedSamples: (fn) => {
                                                    const res =
                                                        typeof fn === 'function'
                                                            ? fn(
                                                                  capturedSamplesRef.current
                                                              )
                                                            : fn
                                                    capturedSamplesRef.current =
                                                        res
                                                    setCapturedSamples(res)
                                                },
                                                updateReduxClassState: (
                                                    samples,
                                                    final,
                                                    category
                                                ) =>
                                                    updateReduxClassState(
                                                        samples,
                                                        final,
                                                        category
                                                    ),
                                            })
                                        )
                                    }}
                                />
                                <img
                                style={{ cursor: 'pointer', width: '50px', height: '50px', marginBottom: '10px' }}
                                src={renderImage('uploadImg')}
                                alt="Upload"/>
                                <p className={styles.webCamModelUploadText}>
                                    Upload audio or drag & drop files here to add samples 
                                </p>
                                {selectedFiles.length > 0 && (
                                    <div className={styles.imageCountDiv} style={{ flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                                        {/* <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>{selectedFiles.length} file selected:</p> */}
                                        <div style={{ 
                                            fontSize: '12px', 
                                            color: '#666', 
                                            maxHeight: '100px', 
                                            overflowY: 'auto', 
                                            width: '80%', 
                                            textAlign: 'left',
                                            padding: '5px',
                                            backgroundColor: 'rgba(255,255,255,0.5)',
                                            borderRadius: '5px',
                                            border: '1px solid #ccc'
                                        }}>
                                            <ul style={{ listStyleType: 'none', padding: 0, margin: 0 }}>
                                                {selectedFiles.map((f, idx) => (
                                                    <li key={idx} style={{ 
                                                        whiteSpace: 'nowrap', 
                                                        overflow: 'hidden', 
                                                        textOverflow: 'ellipsis',
                                                        marginBottom: '2px'
                                                    }}>
                                                        {f.name}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : isModelLoading ? (
                            <p>Loading model...</p>
                        ) : (
                            <RecordingControls
                                isRecording={isRecording}
                                recognizer={recognizer}
                                capturedSamples={capturedSamples}
                                canvasRef={canvasRef}
                                micError={micError}
                                onStart={startRecording}
                                onStop={stopRecording}
                            />
                        )}

                        <SampleGrid
                            samples={capturedSamples}
                            onRemove={removeSample}
                        />
                    </div>
                </div>
            </div>
        )
    )
}

export default AudioModule
